// apps/client/src/hooks/useLenders.ts
// Extract unique lenders from both settlement and commission aggregate data

import { useMemo } from 'react';

interface MonthlySettlement {
  yearMonth: string;
  byLender?: Record<string, { total: number; count: number }>;
}

interface MonthlyAggregate {
  yearMonth: string;
  byLender?: Record<string, any>;
}

/**
 * Extract unique lenders from settlement AND commission aggregate data
 *
 * Aggregates lenders across all months from both data sources to build
 * a comprehensive list for the lender filter dropdown.
 *
 * @param settlements - Array of monthly settlement aggregates
 * @param aggregates - Array of monthly commission aggregates (optional)
 * @returns Sorted array of unique lender names
 */
export function useLenders(
  settlements: MonthlySettlement[],
  aggregates?: MonthlyAggregate[]
): string[] {
  return useMemo(() => {
    const lenderSet = new Set<string>();

    // Extract lenders from settlement records
    settlements.forEach(settlement => {
      if (settlement.byLender) {
        Object.keys(settlement.byLender).forEach(lender => {
          if (lender && lender.trim()) {
            lenderSet.add(lender.trim());
          }
        });
      }
    });

    // ✅ ALSO extract lenders from commission aggregates
    if (aggregates) {
      aggregates.forEach(aggregate => {
        if (aggregate.byLender) {
          Object.keys(aggregate.byLender).forEach(lender => {
            if (lender && lender.trim()) {
              lenderSet.add(lender.trim());
            }
          });
        }
      });
    }

    // Return sorted array
    return Array.from(lenderSet).sort();
  }, [settlements, aggregates]);
}
